/**
 * FORM INPUT WRAPPER
 * Wrapper simplifié pour les champs de formulaire react-hook-form + shadcn/ui
 * Élimine la répétition du pattern FormField + render
 */

import type { Control, FieldPath, FieldValues } from 'react-hook-form';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from './form';
import { Input } from './input';

interface FormInputProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  control: Control<TFieldValues>;
  name: TName;
  label: string;
  placeholder?: string;
  type?: 'text' | 'email' | 'password' | 'number' | 'date' | 'time' | 'datetime-local';
  required?: boolean;
  disabled?: boolean;
  valueAsNumber?: boolean;
  /** Pas de saisie pour type="number" (ex. '0.01' pour les montants en euros) */
  step?: string;
}

export function FormInput<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  control,
  name,
  label,
  placeholder,
  type = 'text',
  required = false,
  disabled = false,
  valueAsNumber = false,
  step,
}: FormInputProps<TFieldValues, TName>) {
  const isDateInput = type === 'date' || type === 'time' || type === 'datetime-local';

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        const isEmpty = !field.value;
        let dateClassName = '';

        if (isDateInput) {
          // Styles communs pour tous les inputs date
          dateClassName =
            'pr-3 [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-100';

          // Styles supplémentaires quand le champ est vide (couleur grise)
          if (isEmpty) {
            dateClassName +=
              ' [&::-webkit-datetime-edit-fields-wrapper]:text-zinc-500 [&::-webkit-datetime-edit-text]:text-zinc-500 [&::-webkit-datetime-edit-month-field]:text-zinc-500 [&::-webkit-datetime-edit-day-field]:text-zinc-500 [&::-webkit-datetime-edit-year-field]:text-zinc-500';
          }
        }

        return (
          <FormItem>
            <FormLabel>
              {label} {required && <span className="text-red-600">*</span>}
            </FormLabel>
            <FormControl>
              <Input
                type={type}
                placeholder={placeholder}
                disabled={disabled}
                step={step}
                {...field}
                value={field.value ?? ''}
                className={dateClassName || undefined}
                onChange={(e) => {
                  if (valueAsNumber) {
                    field.onChange(e.target.valueAsNumber);
                  } else {
                    field.onChange(e);
                  }
                }}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}
